import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

// Initialize Stripe - you'll need to add REACT_APP_STRIPE_PUBLISHABLE_KEY to your .env
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY && 
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY !== 'pk_test_your-stripe-publishable-key'
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

// Card styling to match your theme
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#254e70',
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto',
      '::placeholder': {
        color: '#9CA3AF',
      },
      iconColor: '#254e70',
    },
    invalid: {
      color: '#EF4444',
      iconColor: '#EF4444',
    },
    complete: {
      color: '#22C55E',
      iconColor: '#22C55E',
    },
  },
  hidePostalCode: false,
};

// Internal card form component that has access to Stripe hooks
const CardForm = ({ 
  onCardValidated, 
  onCardError, 
  isSubmitting, 
  userEmail,
  disabled = false,
  validationRef
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Handle real-time card validation
  const handleCardChange = (event) => {
    if (event.error) {
      setCardError(event.error.message);
      onCardError(event.error.message);
    } else {
      setCardError('');
      onCardError('');
    }
    
    setCardComplete(event.complete);
  };

  // Validate the card without charging
  const validateCard = async () => {
    if (!stripe || !elements || !userEmail || processing) {
      return false;
    }

    setProcessing(true);
    setCardError(''); // Clear any previous errors
    
    try {
      // Get the card element
      const cardElement = elements.getElement(CardElement);
      
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Step 1: Create Setup Intent on backend
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      const setupResponse = await fetch(`${API_BASE_URL}/api/payments/create-setup-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userEmail
        }),
      });

      const setupData = await setupResponse.json();
      
      if (!setupResponse.ok) {
        throw new Error(setupData.message || 'Failed to create payment setup');
      }

      // Step 2: Confirm the Setup Intent with the card
      const { error, setupIntent } = await stripe.confirmCardSetup(
        setupData.client_secret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              email: userEmail,
            },
          },
        }
      );

      if (error) {
        throw new Error(error.message || 'Card validation failed');
      }

      // Step 3: Validate on backend
      const validateResponse = await fetch(`${API_BASE_URL}/api/payments/validate-setup-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          setup_intent_id: setupIntent.id,
          payment_method_id: setupIntent.payment_method
        }),
      });

      const validateData = await validateResponse.json();
      
      if (!validateResponse.ok) {
        throw new Error(validateData.message || 'Card validation failed');
      }

      // Success - card is valid!
      setCardError(''); // Clear any previous errors
      onCardValidated({
        stripeCustomerId: validateData.customer_id,
        paymentMethodId: validateData.payment_method.id,
        cardInfo: validateData.payment_method.card,
        validatedAt: validateData.validated_at
      });

      return true;

    } catch (error) {
      console.error('Card validation error:', error);
      setCardError(error.message);
      onCardError(error.message);
      return false;
    } finally {
      setProcessing(false);
    }
  };

  // Expose validation function to parent component
  useEffect(() => {
    if (validationRef && validationRef.current !== undefined) {
      // Store validation function on the ref object
      validationRef.current = { validateCard };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, elements, userEmail, processing, validationRef]);

  return (
    <div className="space-y-4">
      {/* Card Input */}
      <div className="relative">
        <label className="block font-semibold text-[#254e70] text-sm mb-2">
          Credit Card <span className="text-red-500">*</span>
        </label>
        
        <div className={`border-2 rounded-xl p-4 transition-all duration-200 ${
          cardError 
            ? 'border-red-300 bg-red-50' 
            : cardComplete 
            ? 'border-green-300 bg-green-50' 
            : 'border-gray-200 hover:border-[#9dacff] focus-within:border-[#9dacff]'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <CardElement
            options={cardElementOptions}
            onChange={handleCardChange}
            disabled={disabled || isSubmitting}
          />
        </div>

        {/* Validation icons */}
        {(cardError || cardComplete) && (
          <div className="absolute top-9 right-3 flex items-center pointer-events-none">
            {cardError ? (
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        )}
      </div>

      {/* Error message */}
      {cardError && (
        <p className="text-red-600 text-sm flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {cardError}
        </p>
      )}

      {/* Processing indicator */}
      {processing && (
        <div className="flex items-center gap-2 text-[#254e70] text-sm">
          <div className="w-4 h-4 border-2 border-[#254e70] border-t-transparent rounded-full animate-spin"></div>
          <span>Validating card...</span>
        </div>
      )}

      {/* Help text */}
      <div className="bg-white border border-[#e4fde1] rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-6 h-6 bg-[#9dacff]/10 rounded-full flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-[#9dacff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <span className="font-semibold text-[#254e70] text-sm">Card Verification</span>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex items-start gap-3">
            <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-[#254e70]/80">Prevents spam and fake registrations</span>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-[#254e70]"><strong>No charges applied</strong> - verification only</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main wrapper component
const StripeCardCollection = ({ 
  onCardValidated, 
  onCardError, 
  isSubmitting, 
  userEmail,
  disabled = false,
  validationRef
}) => {
  const [stripeError, setStripeError] = useState('');

  // Check if Stripe is properly configured
  useEffect(() => {
    if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
      setStripeError('Stripe is not properly configured. Please contact support.');
    }
  }, []);

  if (stripeError) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
        <p className="text-red-700 text-sm flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {stripeError}
        </p>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <CardForm 
        onCardValidated={onCardValidated}
        onCardError={onCardError}
        isSubmitting={isSubmitting}
        userEmail={userEmail}
        disabled={disabled}
        validationRef={validationRef}
      />
    </Elements>
  );
};

export default StripeCardCollection; 